import { useCallback, useEffect, useState } from "react";

type PushState = "loading" | "unsupported" | "denied" | "prompt" | "subscribed" | "unsubscribed" | "error";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:9000";

async function getVapidPublicKey(): Promise<string | null> {
  try {
    const response = await fetch(`${API_URL}/api/users/push/vapid-key`, {
      credentials: "include",
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { publicKey: string };
    return data.publicKey;
  } catch {
    return null;
  }
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [state, setState] = useState<PushState>("loading");

  const checkCurrentState = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setState("unsupported");
      return;
    }

    const permission = Notification.permission;
    if (permission === "denied") {
      setState("denied");
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setState(subscription ? "subscribed" : "unsubscribed");
    } catch {
      setState("error");
    }
  }, []);

  useEffect(() => {
    void checkCurrentState();
  }, [checkCurrentState]);

  const subscribe = useCallback(async () => {
    try {
      const vapidKey = await getVapidPublicKey();
      if (!vapidKey) {
        setState("error");
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      const subJson = subscription.toJSON();
      const response = await fetch(`${API_URL}/api/users/push/subscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          subscription: {
            endpoint: subJson.endpoint,
            keys: {
              p256dh: subJson.keys?.p256dh,
              auth: subJson.keys?.auth,
            },
          },
        }),
      });

      if (!response.ok) {
        setState("error");
        return false;
      }

      setState("subscribed");
      return true;
    } catch {
      setState("error");
      return false;
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        setState("unsubscribed");
        return true;
      }

      await fetch(`${API_URL}/api/users/push/subscription`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      });

      await subscription.unsubscribe();
      setState("unsubscribed");
      return true;
    } catch {
      setState("error");
      return false;
    }
  }, []);

  return { state, subscribe, unsubscribe };
}
